import { useState } from 'react';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useAxios from '../api/axios';
import { toast } from 'react-toastify';
import { Button as AppButton } from '../components/Button';
import { MUNICIPALITY_OPTIONS } from '../utils/courseLocationData';

const sx = { backgroundColor: 'white', borderRadius: '12px' };

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentYear = new Date().getFullYear();
  const from = location.state?.from?.pathname || '/';

  const [firstname, setFirstName] = useState('');
  const [surname, setSurname] = useState('');
  const [year, setYear] = useState('');
  const [yearError, setYearError] = useState(false);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [sex, setSex] = useState('');
  const [contactShareConsent, setContactShareConsent] = useState(false);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);

  const api = useAxios();

  function validateForm() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    let ok = true;

    if (firstname.trim().length === 0) { toast.error('Syötä etunimesi'); ok = false; }
    if (surname.trim().length === 0) { toast.error('Syötä sukunimesi'); ok = false; }
    if (municipality.trim().length === 0) { toast.error('Valitse paikkakuntasi'); ok = false; }
    if (sex === '') { toast.error('Valitse sukupuoli'); ok = false; }
    if (!emailRegex.test(email)) { toast.error('Tarkista sähköpostiosoite'); ok = false; }
    if (password.length < 8) { toast.error('Salasanan tulee olla vähintään 8 merkkiä'); ok = false; }
    if (password !== passwordConfirm) { toast.error('Salasanat eivät täsmää'); ok = false; }
    if (!contactShareConsent) { toast.error('Hyväksy yhteystietojen näyttäminen seuraavalle vastustajalle'); ok = false; }
    if (!privacyPolicyAccepted) { toast.error('Hyväksy tietosuojaseloste'); ok = false; }

    return ok;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await api.post(
        '/login.php?action=register',
        JSON.stringify({
          firstname,
          surename: surname,
          year,
          phone,
          municipality,
          email,
          password,
          sex,
          contactShareConsent,
          privacyPolicyAccepted,
        }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      navigate(from, { replace: true });
      toast.info('Rekisteröinti onnistui');
    } catch (err) {
      if (!err?.response) {
        toast.error('Ei vastausta palvelimelta.');
      } else if (err.response?.status === 400) {
        toast.error(err.response.data.msg);
      } else if (err.response?.status === 409) {
        toast.error(err.response.data.msg);
      } else {
        toast.error('Rekisteröinti epäonnistui');
      }
    }
  };

  return (
    <div className="mx-auto max-w-xl px-4 pb-12 pt-24">
      <div className="mb-6 rounded-[28px] border border-white/10 bg-slate-900/70 p-6 shadow-2xl backdrop-blur">
        <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Tili</p>
        <h1 className="mt-2 text-3xl font-black text-white">Rekisteröidy</h1>
        <p className="mt-2 text-sm leading-6 text-slate-300">Luo tunnus osallistuaksesi kilpailuihin.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="rounded-[28px] border border-white/10 bg-slate-900/70 p-6 shadow-2xl backdrop-blur">

          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">Henkilötiedot</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              variant="filled" required label="Etunimi" autoFocus
              autoComplete="given-name" value={firstname}
              onChange={(e) => setFirstName(e.target.value)}
              sx={sx} fullWidth
            />
            <TextField
              variant="filled" required label="Sukunimi"
              autoComplete="family-name" value={surname}
              onChange={(e) => setSurname(e.target.value)}
              sx={sx} fullWidth
            />
            <TextField
              variant="filled" required label="Syntymävuosi" type="number"
              value={year}
              onChange={(e) => {
                const v = e.target.value;
                setYear(v);
                setYearError(v !== '' && (Number(v) < 1950 || Number(v) > currentYear));
              }}
              error={yearError}
              helperText={yearError ? `Vuoden tulee olla välillä 1950–${currentYear}` : ''}
              inputProps={{ min: 1950, max: currentYear }}
              sx={sx} fullWidth
            />
            <FormControl variant="filled" required sx={sx} fullWidth>
              <InputLabel id="sex-label">Sukupuoli</InputLabel>
              <Select labelId="sex-label" value={sex} onChange={(e) => setSex(e.target.value)}>
                <MenuItem value="Mies">Mies</MenuItem>
                <MenuItem value="Nainen">Nainen</MenuItem>
              </Select>
            </FormControl>
            <TextField
              variant="filled" label="Puhelin"
              autoComplete="tel" value={phone}
              onChange={(e) => setPhone(e.target.value)}
              sx={sx} fullWidth
            />
            <TextField
              variant="filled" required label="Paikkakunta"
              autoComplete="address-level2" value={municipality}
              onChange={(e) => setMunicipality(e.target.value)}
              sx={sx} fullWidth
              select
            >
              <MenuItem value="" disabled>Valitse paikkakunta</MenuItem>
              {MUNICIPALITY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </TextField>
          </div>

          <p className="mb-4 mt-6 text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">Kirjautumistiedot</p>
          <div className="grid gap-4">
            <TextField
              variant="filled" required label="Sähköposti" type="email"
              autoComplete="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={sx} fullWidth
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                variant="filled" required label="Salasana" type="password"
                autoComplete="new-password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={sx} fullWidth
              />
              <TextField
                variant="filled" required label="Salasana uudelleen" type="password"
                autoComplete="new-password" value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                error={passwordConfirm.length > 0 && password !== passwordConfirm}
                helperText={passwordConfirm.length > 0 && password !== passwordConfirm ? 'Salasanat eivät täsmää' : ''}
                sx={sx} fullWidth
              />
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-4 text-sm text-slate-200">
            <p className="font-semibold text-cyan-200">Yhteystietojen käyttö</p>
            <p className="mt-2 leading-6 text-slate-300">
              Puhelinnumerosi ja sähköpostiosoitteesi eivät ole yleisesti näkyvillä sivustolla. Ne näytetään vain
              seuraavalle vastustajallesi, jotta voitte sopia peliajan.
            </p>
          </div>

          <div className="mt-5 grid gap-1 rounded-2xl border border-white/10 bg-slate-950/40 p-4">
            <FormControlLabel
              control={(
                <Checkbox
                  checked={contactShareConsent}
                  onChange={(e) => setContactShareConsent(e.target.checked)}
                  sx={{ color: 'rgba(103, 232, 249, 0.7)', '&.Mui-checked': { color: '#67e8f9' } }}
                />
              )}
              label={(
                <span className="text-sm leading-6 text-slate-200">
                  Annan luvan siihen, että puhelinnumeroni ja sähköpostiosoitteeni näytetään seuraavalle
                  vastustajalleni pelien sopimista varten.
                </span>
              )}
            />
            <FormControlLabel
              control={(
                <Checkbox
                  checked={privacyPolicyAccepted}
                  onChange={(e) => setPrivacyPolicyAccepted(e.target.checked)}
                  sx={{ color: 'rgba(103, 232, 249, 0.7)', '&.Mui-checked': { color: '#67e8f9' } }}
                />
              )}
              label={(
                <span className="text-sm leading-6 text-slate-200">
                  Olen lukenut ja hyväksyn{' '}
                  <Link
                    to="/tietosuojaseloste"
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-cyan-300 underline hover:text-cyan-200"
                  >
                    tietosuojaselosteen
                  </Link>
                  .
                </span>
              )}
            />
          </div>

          <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
            <Link
              to="/tietosuojaseloste"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-slate-400 underline hover:text-slate-200"
            >
              Tietosuojaseloste
            </Link>
            <div className="flex gap-3">
              <AppButton name="Takaisin" type="SECONDARY" action={() => navigate('/')} />
              <AppButton name="Rekisteröidy" type="PRIMARY" buttonType="submit" />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Register;
